<template>
  <div class="card full-height">
    <div class="card-header">
      <h3>Новостная активность источников</h3>
      <div class="card-actions">
        <RefreshIcon @click="refresh" />
        <CalendarInput v-model="localDate" />
        <div 
          v-click-outside="closeDropdown"
          class="select-wrapper" 
        >
          <div 
            class="select-trigger" 
            @click="toggleDropdown"
          >
            <span 
              class="select-label"
              :class="{ active: isDropdownOpen }"
            >
              Источники
            </span>
            <span class="select-value">{{ selectedFilter }}</span>
            <ChevronDown 
              class="select-icon" 
              :class="{ rotated: isDropdownOpen }" 
            />
          </div>
          
          <div 
            v-if="isDropdownOpen" 
            class="select-dropdown"
          >
            <div 
              v-for="option in filterOptions" 
              :key="option"
              class="select-option"
              :class="{ active: option === selectedFilter }"
              @click="selectFilter(option)"
            >
              {{ option }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Таблица -->
    <div class="table-wrapper">
      <table class="activity-table">
        <thead>
          <tr>
            <th>Источник</th>
            <th class="activity-table__th">
              <div class="th-content">
                Количество новостей
                <div 
                  v-click-outside="closeSortDropdown"
                  class="sort-wrapper"
                >
                  <button 
                    @click="toggleSortDropdown"
                  >
                    <SortButtonIcon :class="{ 'sort-button--active': isSortDropdownOpen }" />
                  </button>
                  
                  <div 
                    v-if="isSortDropdownOpen" 
                    class="sort-dropdown"
                  >
                    <div 
                      class="sort-option"
                      :class="{ active: sortOrder === 'desc' }"
                      @click="selectSort('desc')"
                    >
                      <SortIconActive 
                        class="sort-icon" 
                      />
                      по убыванию кол-ва
                    </div>
                    <div 
                      class="sort-option"
                      :class="{ active: sortOrder === 'asc' }"
                      @click="selectSort('asc')"
                    >
                      <SortIcon 
                        class="sort-icon" 
                      />
                      по возрастанию кол-ва
                    </div>
                  </div>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr
            v-for="i in 10"
            :key="i"
          >
            <td
              colspan="2"
              class="skeleton-td"
            />
          </tr>
        </tbody>
        <tbody v-else-if="tableData.length">
          <tr
            v-for="source in tableData"
            :key="source.sourceId"
          >
            <td>{{ source.name ? source.name : source.sourceId }}</td>
            <td class="activity-table__th">
              {{ source.count }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Пагинация -->
    <div 
      v-if="totalPages > 1" 
      class="pagination"
    >
      <button 
        class="pagination-btn"
        :disabled="currentPage === 1"
        @click="changePage(currentPage - 1)"
      >
        <ChevronRight class="icon-prev" />
      </button>

      <button 
        v-for="page in visiblePages" 
        :key="page"
        class="pagination-item"
        :class="{ active: page === currentPage, dots: page === '...' }"
        @click="changePage(page)"
      >
        {{ page }}
      </button>

      <button 
        class="pagination-btn"
        :disabled="currentPage === totalPages"
        @click="changePage(currentPage + 1)"
      >
        <ChevronRight />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from "vue";
import ChevronDown from "@/components/icons/ChevronDown.vue";
import ChevronRight from "@/components/icons/ChevronRight.vue";
import CalendarInput from "@/components/UI/CalendarInput.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import SortButtonIcon from "@/components/icons/SortButtonIcon.vue";
import SortIconActive from "@/components/icons/SortIconActive.vue";
import SortIcon from "@/components/icons/SortIcon.vue";
import { useAnalyticsStore } from "@/stores/analytics";
import { formatDateForRequest } from "@/tools/time";

const analyticsStore = useAnalyticsStore();

const filterOptions = ["Все", "Пустые"];
const selectedFilter = ref("Все");
const isDropdownOpen = ref(false);
const isSortDropdownOpen = ref(false);
const sortOrder = ref("desc");
const currentPage = ref(1);
const limit = 20;

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

const toggleSortDropdown = () => {
  isSortDropdownOpen.value = !isSortDropdownOpen.value;
};

const closeSortDropdown = () => {
  isSortDropdownOpen.value = false;
};

const selectSort = (order) => {
  sortOrder.value = order;
  closeSortDropdown();
  fetchData();
};

const selectFilter = (option) => {
  selectedFilter.value = option;
  closeDropdown();
};

const localDate = ref([null, null]);

const fetchData = () => {
  const params = {
    page: currentPage.value,
    limit: limit,
    sort_by: "count",
    sort_direction: sortOrder.value
  };

  if (localDate.value?.[0] && localDate.value?.[1]) {
    params.from = formatDateForRequest(localDate.value[0]);
    params.to = formatDateForRequest(localDate.value[1]);
  }

  analyticsStore.fetchSourcesActivity(params);
};

watch(localDate, (newVal) => {
  if (newVal?.[0] && newVal?.[1]) {
    currentPage.value = 1;
    fetchData();
  }
});

watch(currentPage, () => {
  fetchData();
});

const refresh = () => {
  fetchData();
};

const isLoading = computed(() => analyticsStore.isSourcesActivityLoading);

const sourcesList = computed(() => analyticsStore.sourcesActivity || {});
const totalPages = computed(() => sourcesList.value.total_pages || 0);

const tableData = computed(() => {
  const data = sourcesList.value.results || [];
  if (selectedFilter.value === "Пустые") {
    return data.filter(source => source.count === 0);
  }
  return data;
});

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const delta = 1;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
});

const changePage = (page) => {
  if (page === "...") return;
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

onMounted(() => {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  localDate.value = [sevenDaysAgo, today];
});
</script>

<style scoped>
.card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.full-height {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 24px 24px 0;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #1A1D1F;
}

.card-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.select-wrapper {
  width: 175px;
  position: relative;
  font-size: 13px;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: #F9FAFB;
  border: 1px solid #EFEFEF;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.2s;
}

.select-trigger:hover {
  border-color: #DEDEDE;
}

.select-label.active {
  color: #1F9AFF;
}

.select-value {
  color: #7B87A5;
}

.select-icon {
  width: 16px;
  height: 16px;
  color: #7B87A5;
  transition: transform 0.2s;
}

.select-icon.rotated {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 100%;
  width: max-content;
  background: white;
  border: 1px solid #EFEFEF;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  overflow: hidden;
  padding: 4px;
}

.select-option {
  padding: 8px 12px;
  cursor: pointer;
  color: #1A1D1F;
  border-radius: 6px;
  transition: background 0.2s;
}

.select-option:hover {
  background: #F4F4F4;
}

.select-option.active {
  background: #EAF6FF;
  color: #1F9AFF;
}

/* Таблица */
.table-wrapper {
  flex: 1;
  padding: 0 24px;
  overflow: hidden;
}

.activity-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #F7F8FD;
  display: flex;
  flex-direction: column;
}

.activity-table thead {
  flex: 0 0 auto;
  width: 100%;
}

.activity-table tbody {
  flex: 1 1 auto;
  display: block;
  overflow-y: auto;
  width: 100%;
  max-height: 657px;
}

.activity-table tbody::-webkit-scrollbar {
  width: 6px;
}

.activity-table tbody::-webkit-scrollbar-track {
  background: transparent;
}

.activity-table tbody::-webkit-scrollbar-thumb {
  background: #CDE4F8;
  border-radius: 3px;
}

.activity-table tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

.activity-table th {
  text-align: left;
  color: #9A9FA5;
  font-size: 14px;
  font-weight: 500;
  padding: 12px 16px;
  background: #F7F8FD;
}

.activity-table th:first-child,
.activity-table td:first-child {
  width: 60%;
}

.activity-table th:last-child,
.activity-table td:last-child {
  width: 40%;
}

.activity-table {
  border-radius: 8px 8px 0 0;
}

.activity-table td {
  padding: 16px 16px;
  font-size: 16px;
  color: #373D3F;
  border: 1px solid #F4F4F4;
  font-weight: 500;
}

.activity-table__th {
  text-align: right;
  white-space: nowrap;
}

td.activity-table__th {
  font-weight: 400;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 12px;
  margin: 0 24px 20px 24px;
  background: #F7F8FD;
  border-radius: 0 0 8px 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #7B87A5;
  border-radius: 8px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  color: #1A1D1F;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-item {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #7B87A5;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.pagination-item:hover:not(.dots):not(.active) {
  color: #1A1D1F;
}

.pagination-item.active {
  background: white;
  color: #1A1D1F;
}

.pagination-item.dots {
  cursor: default;
}

.icon-prev {
  transform: rotate(180deg);
}

.skeleton-td {
  background: linear-gradient(90deg, #ffffff 25%, #f7f8fd 50%, #ffffff 75%);
  background-size: 200% 100%;
  height: 64px; /* Approximate height of a row */
  animation: skeleton-loading 1.5s infinite;
  border: 1px solid #F4F4F4;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.th-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  line-height: 100%;
}

.sort-wrapper {
  position: relative;
}

.sort-btn:hover, .sort-btn.active {
  background: #EAF6FF;
  color: #1F9AFF;
}

.sort-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: max-content;
  background: white;
  border: 1px solid #EFEFEF;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 6px;
  min-width: 200px;
}

.sort-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  color: #373D3F;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  transition: background 0.2s;
}

.sort-option:hover {
  background: #F4F4F4;
}

.sort-option.active {
  color: #1F9AFF;
}

.sort-icon {
  color: #CACACA;
}

.sort-option.active .sort-icon {
  color: #1F9AFF;
}

.sort-button--active {
  background: #FFFFFF;
  color: #1F9AFF;
  border-radius: 20px;
}
</style>
